import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { formatNumber } from "../../../../shared/utils/numberFormatter";
import { makeFormatValue, getThresholdValues } from "../utils/axisFormat";

/**
 * Mixpanel-style single funnel bar. Uses chart config (axis.leftY, colors) like Insights when config is provided.
 */
export default function FunnelSummaryBarChart({ steps = [], config }) {
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const xAxis = config?.axis?.xAxis ?? {};
  const funnelAxis = config?.funnelAxis ?? "left";
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);
  const { min: minThreshold, max: maxThreshold } = getThresholdValues(yAxisConfig);
  const leftYVisible = yAxisConfig.visible !== false;
  const leftYScale =
    yAxisConfig && String(yAxisConfig.scale) === "log" ? "log" : "linear";
  const leftYLabel = yAxisConfig.label;
  const outOfBoundsMode = yAxisConfig.outOfBounds ?? "hidden"; // "hidden" = cropped view; "visible" = full range
  const xAxisVisible = xAxis.visible !== false;
  const xAxisLabel = xAxis.label || "";
  const barColor = config?.colors?.[0] ?? "#B39DFF";
  if (!Array.isArray(steps) || steps.length === 0) {
    return (
      <div className="flex items-center justify-center h-[240px]">
        <div className="text-sm" style={{ color: theme.textMuted }}>
          No data available
        </div>
      </div>
    );
  }

  const firstStep = steps[0];
  const lastStep = steps[steps.length - 1];

  const firstCount = Number(firstStep.count || 0);
  const lastCount = Number(lastStep.count || 0);

  // Prefer backend overallConversion if present, else compute from counts
  const overallConversion =
    typeof firstStep.overallConversion === "number"
      ? firstStep.overallConversion
      : firstCount > 0
      ? (lastCount / firstCount) * 100
      : 0;

  const hasThresholds = minThreshold != null || maxThreshold != null;

  // Base domain from data (do NOT clamp the data itself)
  const autoMax = Math.max(100, overallConversion * 1.2);

  let xDomain;
  if (hasThresholds && outOfBoundsMode === "hidden") {
    // Clip viewport to [Min, Max]; let Recharts crop the bar visually
    const baseMin = leftYScale === "log" ? 0.01 : 0;
    const min = minThreshold != null ? minThreshold : baseMin;
    const max = maxThreshold != null ? maxThreshold : autoMax;
    const safeMin = leftYScale === "log" ? Math.max(0.01, min) : min;
    const safeMax =
      leftYScale === "log" ? Math.max(safeMin * 1.1, max) : Math.max(safeMin, max);
    xDomain = [safeMin, safeMax];
  } else {
    // Visible or no thresholds: show full data range starting at 0 (or log floor)
    const baseMin = leftYScale === "log" ? 0.01 : 0;
    const safeMin = minThreshold != null ? minThreshold : baseMin;
    const safeMax = Math.max(autoMax, maxThreshold ?? autoMax);
    xDomain = [safeMin, safeMax];
  }

  const xAxisLabelProps = leftYLabel ? { label: { value: leftYLabel, position: "insideBottom", offset: -8, style: { fontSize: 12, fill: "#6B7280" } } } : {};

  const title = `Conversion Rate of ${
    firstStep.label || firstStep.eventName || "step 1"
  } through ${lastStep.label || lastStep.eventName || `step ${steps.length}`} \u2013 All Steps`;

  const data = [
    {
      name: "Conversion Rate",
      value: overallConversion,      // raw data; clipping happens via axis domain
      users: lastCount,
      __actualValue: overallConversion,
    },
  ];

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload || {};
    const value = point.__actualValue != null ? Number(point.__actualValue) : Number(point.value || 0);
    const users = Number(point.users || 0);

    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          border: `1px solid ${theme.border}`,
          padding: "12px 16px",
          minWidth: 240,
          fontFamily: "Raleway, sans-serif",
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: theme.textPrimary,
            marginBottom: 4,
            lineHeight: 1.4,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: theme.textPrimary,
            marginBottom: 2,
          }}
        >
          {formatYValue(value)} converted
        </div>
        <div
          style={{
            fontSize: 13,
            color: theme.textPrimary,
            marginBottom: 4,
          }}
        >
          {formatNumber(users)} users
        </div>
        <div
          style={{
            fontSize: 12,
            color: theme.textMuted,
          }}
        >
          100% of overall
        </div>
      </div>
    );
  };

  return (
    <div className="w-full" style={{ height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 16, right: 24, left: 24, bottom: 16 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(0,0,0,0.08)"
            horizontal={false}
          />
          <XAxis
            type="number"
            hide={!leftYVisible}
            domain={xDomain}
            scale={leftYScale}
            // Hidden + thresholds => enforce [Min, Max] and crop bar; otherwise let Recharts extend to fit
            allowDataOverflow={hasThresholds && outOfBoundsMode === "hidden"}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
            axisLine={{ stroke: "rgba(0,0,0,0.15)" }}
            tickLine={false}
            tickFormatter={formatYValue}
            {...xAxisLabelProps}
          />
          <YAxis
            type="category"
            dataKey="name"
            hide={!xAxisVisible}
            {...(xAxisLabel ? {
              label: {
                value: xAxisLabel,
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 12, fill: "#6B7280" }
              }
            } : {})}
          />
          <Tooltip content={<TooltipContent />} />
          <Bar
            dataKey="value"
            fill={barColor}
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

